import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faStickyNote } from "@fortawesome/pro-solid-svg-icons/faStickyNote";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import { AnyEntityId, EntityTypeSupportingNotes } from "@mapmycustomers/shared/types/entity";
import Note from "@mapmycustomers/shared/types/entity/common/Note";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { Modal, TextAreaWithMentions } from "@mapmycustomers/ui";

import { getAllTeamsOfEntireOrganization, getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import useSourceObjectMessageArgs from "@app/util/hook/useSourceObjectMessageArgs";

import styles from "./AddNoteModal.module.scss";
import EntityLabel from "./component/EntityLabel";
import { isCreating } from "./store";
import { createNote } from "./store/actions";

interface Props {
  allUsers: User[];
  analyticIssuer?: AnalyticsService;
  createNote: typeof createNote.request;
  creating: boolean;
  entityId: AnyEntityId;
  entityName: string;
  entityType: EntityTypeSupportingNotes;
  onHide: (added: boolean, note?: Note) => void;
  sourceObject?: HasSourceObject["sourceObject"];
  teams: Team[];
}

const AddNoteModal = ({
  allUsers,
  analyticIssuer,
  createNote,
  creating,
  entityId,
  entityName,
  entityType,
  onHide,
  sourceObject,
  teams,
}: Props) => {
  const intl = useIntl();
  const sourceObjectArgs = useSourceObjectMessageArgs(entityType, sourceObject);

  const [note, setNote] = useState<string>("");
  const handleHideClick = useCallback(() => onHide(false), [onHide]);

  const handleCreateClick = useCallback(() => {
    createNote({
      callback: (note: Note) => {
        onHide(true, note);
        analyticIssuer?.clicked(["Note Created"], { entityId, entityType, noteId: note.id });
      },
      entityId,
      entityType,
      note,
    });
  }, [analyticIssuer, createNote, entityId, entityType, onHide, note]);

  return (
    <Modal
      className={styles.container}
      okButtonProps={{
        disabled: creating || !note,
        icon: <FontAwesomeIcon icon={faStickyNote} />,
        loading: creating,
      }}
      okText={intl.formatMessage({
        id: "addNoteModal.footer.createNote",
        defaultMessage: "Add Note",
        description: "Add button title on Add Note modal",
      })}
      onCancel={handleHideClick}
      onOk={handleCreateClick}
      open
      title={intl.formatMessage(
        {
          id: "addNoteModal.header",
          defaultMessage:
            "Add Note to {sourceObject, select, standard {{entityType, select, accounts {Company} contacts {Person} deals {Deal} other {Record}}} leadCompanies {Lead} siteCompanies {Site} projectCompanies {Project} shopCompanies {Shop} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}}",
          description:
            'Header of Add Note modal. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
        },
        {
          ...sourceObjectArgs,
          entityType,
        }
      )}
    >
      <section className={styles.verticalSpace}>
        <EntityLabel
          entityName={entityName}
          entityType={entityType}
          sourceObjectArgs={sourceObjectArgs}
        />
        <TextAreaWithMentions
          allUsers={allUsers}
          hideDisabledUsers
          label={intl.formatMessage({
            id: "addNoteModal.field.note",
            defaultMessage: "Note",
            description: "Note field on the Add Note modal",
          })}
          maxLength={Number.MAX_SAFE_INTEGER}
          onChange={setNote}
          rows={4}
          teams={teams}
          value={note}
        />
      </section>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  allUsers: getUsersOfEntireOrganization(state),
  creating: isCreating(state),
  teams: getAllTeamsOfEntireOrganization(state),
});

const mapDispatchToProps = {
  createNote: createNote.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddNoteModal);
